import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBreakpointValue } from '@chakra-ui/react';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { useAuth } from '../../contexts/AuthContext';
import { MAIN_TOUR_STEPS, WATCH_TOUR_STEPS, DEMO_TOUR_STEPS, TourStep } from './tourSteps';
import { waitForElement } from './waitForElement';

interface TourContextValue {
  /** Replay the main (Agents → Watches) tour. */
  start: () => void;
  /** Replay the watch-detail tour. */
  startWatchTour: () => void;
  /** Start the watch-detail tour once, on first view (no-op if already seen). */
  maybeAutoStartWatchTour: () => void;
  /** Replay the demo-watch tour (teaches plot-reading on /projects/demo). */
  startDemoTour: () => void;
  /** Start the demo tour once, on first demo view (no-op if already seen). */
  maybeAutoStartDemoTour: () => void;
}

const TourContext = createContext<TourContextValue>({
  start: () => {},
  startWatchTour: () => {},
  maybeAutoStartWatchTour: () => {},
  startDemoTour: () => {},
  maybeAutoStartDemoTour: () => {},
});

export const useTour = () => useContext(TourContext);

const mainKey = (userId: string) => `aw_hasSeenTour_${userId}`;
const watchKey = (userId: string) => `aw_hasSeenWatchTour_${userId}`;
// The demo is reachable signed-out, so fall back to an 'anon' key.
const demoKey = (userId?: string) => `aw_hasSeenDemoTour_${userId ?? 'anon'}`;

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { backendUser } = useAuth();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Refs so the imperative tour callbacks always read current values.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;
  const userIdRef = useRef<string | undefined>(backendUser?.id);
  userIdRef.current = backendUser?.id;

  const driverRef = useRef<Driver | null>(null);
  // The steps + persistence key of the tour currently running.
  const activeStepsRef = useRef<TourStep[]>([]);
  const activeSeenKeyRef = useRef<string | null>(null);
  const mainAutoStartedRef = useRef<string | null>(null);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const persistSeen = () => {
    const key = activeSeenKeyRef.current;
    if (!key) return;
    try {
      localStorage.setItem(key, 'true');
    } catch {
      /* ignore storage failures */
    }
  };

  // Navigate to a step's route (if needed) and wait for its anchor. Returns the
  // index actually landed on after skipping any steps whose anchor never appears.
  const resolveStep = async (start: number, dir: number): Promise<number> => {
    const steps = activeStepsRef.current;
    let i = start;
    while (i >= 0 && i < steps.length) {
      const step = steps[i];
      if (step.route && locationRef.current.pathname !== step.route) {
        navigateRef.current(step.route);
      }
      if (step.before) {
        try {
          await step.before();
        } catch {
          /* ignore before-hook failures */
        }
      }
      if (!step.element) return i; // centered card, nothing to wait for
      const el = await waitForElement(step.element, step.timeoutMs ?? 4000).catch(
        () => null
      );
      if (el) return i;
      i += dir; // anchor missing → skip
    }
    return -1;
  };

  const finish = () => {
    persistSeen();
    driverRef.current?.destroy();
  };

  const buildDriver = (): Driver =>
    driver({
      allowClose: true,
      animate: !prefersReducedMotion,
      overlayColor: 'rgba(10, 20, 40, 0.55)',
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: 'aw-tour',
      showProgress: true,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      steps: activeStepsRef.current.map((s) => ({
        element: s.element,
        popover: {
          title: s.title,
          description: s.description,
          side: s.side,
          align: s.align,
        },
      })),
      onNextClick: async () => {
        const current = driverRef.current?.getActiveIndex() ?? 0;
        const target = await resolveStep(current + 1, 1);
        if (target === -1) finish();
        else driverRef.current?.moveTo(target);
      },
      onPrevClick: async () => {
        const current = driverRef.current?.getActiveIndex() ?? 0;
        const target = await resolveStep(current - 1, -1);
        if (target !== -1) driverRef.current?.moveTo(target);
      },
      onCloseClick: () => finish(),
      onDestroyed: () => persistSeen(),
    });

  const runTour = async (steps: TourStep[], seenKeyName: string) => {
    driverRef.current?.destroy();
    activeStepsRef.current = steps;
    activeSeenKeyRef.current = seenKeyName;
    driverRef.current = buildDriver();
    const target = await resolveStep(0, 1);
    if (target === -1) return;
    driverRef.current.drive(target);
  };

  const maybeAutoStart = (steps: TourStep[], seenKeyName: string) => {
    if (isMobile) return;
    if (driverRef.current?.isActive()) return;
    let seen = true;
    try {
      seen = !!localStorage.getItem(seenKeyName);
    } catch {
      seen = true;
    }
    if (seen) return;
    setTimeout(() => {
      if (driverRef.current?.isActive()) return;
      runTour(steps, seenKeyName);
    }, 800);
  };

  // Keep latest closures available to the stable context value.
  const apiRef = useRef({
    start: () => {},
    startWatchTour: () => {},
    maybeAutoStartWatchTour: () => {},
    startDemoTour: () => {},
    maybeAutoStartDemoTour: () => {},
  });
  apiRef.current = {
    start: () => {
      const id = userIdRef.current;
      if (id) runTour(MAIN_TOUR_STEPS, mainKey(id));
    },
    startWatchTour: () => {
      const id = userIdRef.current;
      if (id) runTour(WATCH_TOUR_STEPS, watchKey(id));
    },
    maybeAutoStartWatchTour: () => {
      const id = userIdRef.current;
      if (id) maybeAutoStart(WATCH_TOUR_STEPS, watchKey(id));
    },
    // Demo tour works signed-out too (anon key).
    startDemoTour: () => runTour(DEMO_TOUR_STEPS, demoKey(userIdRef.current)),
    maybeAutoStartDemoTour: () =>
      maybeAutoStart(DEMO_TOUR_STEPS, demoKey(userIdRef.current)),
  };

  // Auto-start the main tour once on first login (desktop only).
  useEffect(() => {
    if (isMobile) return;
    const id = backendUser?.id;
    if (!id) return;
    const key = mainKey(id);
    if (mainAutoStartedRef.current === key) return;
    mainAutoStartedRef.current = key;
    maybeAutoStart(MAIN_TOUR_STEPS, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser?.id, isMobile]);

  // Teardown on unmount.
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      start: () => apiRef.current.start(),
      startWatchTour: () => apiRef.current.startWatchTour(),
      maybeAutoStartWatchTour: () => apiRef.current.maybeAutoStartWatchTour(),
      startDemoTour: () => apiRef.current.startDemoTour(),
      maybeAutoStartDemoTour: () => apiRef.current.maybeAutoStartDemoTour(),
    }),
    []
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
